import React from 'react'

import { defaultSchema } from 'hast-util-sanitize'
import type { Components } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import directive from 'remark-directive'
import remarkGfm from 'remark-gfm'
import remark2rehype from 'remark-rehype'
import { twMerge } from 'tailwind-merge'

import { COMMON_COMPONENTS } from '@/components/common/TextArea/MarkdownTextArea/commonComponents'
import { htmlDirectives } from '@/components/common/TextArea/MarkdownTextArea/lib'
import Markdown from '@/components/markdown/Markdown'

const customSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'mention',
    'mark',
    'u',
    'sup',
    'sub',
    'blockquote',
    'span',
  ],
  attributes: {
    ...(defaultSchema.attributes || {}),
    p: ['className'],
    span: ['className'],
  },
}

interface RenderedTextArea {
  children?: string
  className?: string
  components?: Partial<Components> | null
  dangerouslyParseNewLines?: boolean
}

const COMMON_MARKDOWN_COMPONENTS = COMMON_COMPONENTS as Partial<Components>

const mergeMarkdownComponents = (
  components: Partial<Components> | null | undefined,
): Partial<Components> => {
  if (!components) {
    return COMMON_MARKDOWN_COMPONENTS
  }
  return {
    ...COMMON_MARKDOWN_COMPONENTS,
    ...components,
  }
}

export const removeInvalidMarkdownLinks = (markdown: string | undefined) => {
  if (!markdown || typeof markdown !== 'string') {
    return markdown
  }
  return markdown.replace(/\[([^\]]+)\]\(([^)]*)\)/g, (match, name, url) => {
    if (!url.trim()) {
      return name // URL is empty, return just the name
    }
    try {
      new URL(url)
      return match // URL is valid, return the original link
    } catch {
      return name // URL is invalid, return just the name
    }
  })
}

export const parseMarks = (markdown: string) => {
  if (typeof markdown !== 'string') {
    return markdown
  }
  return markdown.replace(/==([^=]+)==/g, '<mark>$1</mark>')
}

export const insertNewLines = (markdown: string) => {
  let result = ''
  let i = 0
  while (i < markdown.length) {
    const char = markdown[i]

    if (char.charCodeAt(0) === 10 && i > 0 && markdown[i - 1] !== '|') {
      // we found a "real" paragraph break
      if (
        // check for triple new lines since a typical new line will look like
        // [blahblahblah\n][\n\n][...], where the first bracket is the previous line, the second bracket
        // is the blank new line itself, and the third bracket is part of the next line;
        // if the previous and next characters are new lines, we should add an invisible character
        // to force a new paragraph;
        // TODO: this is a hack and we should eventually come up with a better way to handle this
        markdown[i - 1].charCodeAt(0) === 10 &&
        i < markdown.length - 1 &&
        markdown[i + 1].charCodeAt(0) === 10
      ) {
        // we found a double newline, so we should add an invisible character to force a new paragraph
        result += `\n\u3000\n\n`
        i += 1
      } else {
        result += char
      }
    } else {
      result += char
    }
    i += 1
  }
  return result
}

export const RenderedTextArea: React.FC<RenderedTextArea> = ({
  children,
  className,
  components,
  dangerouslyParseNewLines = false,
}) => {
  let cleanedMarkdown = removeInvalidMarkdownLinks(children)
  cleanedMarkdown = parseMarks(cleanedMarkdown ?? '')
  cleanedMarkdown = dangerouslyParseNewLines
    ? insertNewLines(cleanedMarkdown)
    : cleanedMarkdown
  const mergedComponents = React.useMemo(
    () => mergeMarkdownComponents(components),
    [components],
  )
  return (
    <Markdown
      rehypePlugins={[
        remark2rehype,
        rehypeRaw,
        // need this to make sure rehypeSanitize doesn't remove the mention tag
        [rehypeSanitize, customSchema],
      ]}
      remarkPlugins={[remarkGfm, directive, htmlDirectives]}
      className={twMerge(
        'text-sm text-primary-900 dark:text-primary-100',
        className,
      )}
      components={mergedComponents}
    >
      {cleanedMarkdown}
    </Markdown>
  )
}
